import moment from "moment";

export const SESSION_STATE_RUNNING = "st202406071447279901";
export const SESSION_STATE_PAUSED = "st202406071447279902";
export const SESSION_STATE_FINALIZED = "st202406071447279903";

export function getSessionStateId(session) {
  if (!session) return null;
  return (
    session.state_te?.st_id ||
    session.tbl_state_te?.st_id ||
    session.te_state ||
    null
  );
}

export function isSessionFinalized(session) {
  return getSessionStateId(session) === SESSION_STATE_FINALIZED;
}

export function isSessionLive(session) {
  const id = getSessionStateId(session);
  return id === SESSION_STATE_RUNNING || id === SESSION_STATE_PAUSED;
}

/** Local calendar day of a session date (YYYY-MM-DD). */
export function sessionDateKey(session) {
  if (!session?.ts_date) return null;
  const day = moment(session.ts_date);
  if (!day.isValid()) return null;
  return day.format("YYYY-MM-DD");
}

/** Group sessions scheduled today or tomorrow (player RSVP window). */
export function isSessionTodayOrTomorrow(session, now = new Date()) {
  const sessionKey = sessionDateKey(session);
  if (!sessionKey) return false;
  const today = moment(now).format("YYYY-MM-DD");
  const tomorrow = moment(now).add(1, "day").format("YYYY-MM-DD");
  return sessionKey === today || sessionKey === tomorrow;
}

export function parseSessionWindow(session, nowMs = Date.now()) {
  if (!session?.ts_date) return null;
  const datePart = moment(session.ts_date).format("YYYY-MM-DD");
  const timeRaw = session.ts_start_time || "00:00:00";
  const timePart = String(timeRaw).slice(0, 8);
  const start = moment(`${datePart} ${timePart}`, ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD HH:mm"], true);
  if (!start.isValid()) return null;
  const durationMin = Number(session.ts_duration) || 90;
  const end = start.clone().add(durationMin, "minutes");
  return {
    startMs: start.valueOf(),
    endMs: end.valueOf(),
    durationMin,
    nowMs,
  };
}

export function isWithinSessionWindow(session, nowMs = Date.now()) {
  const w = parseSessionWindow(session, nowMs);
  if (!w) return false;
  return nowMs >= w.startMs && nowMs <= w.endMs;
}

export function sessionWindowRemainingMs(session, nowMs = Date.now()) {
  const w = parseSessionWindow(session, nowMs);
  if (!w) return 0;
  return Math.max(0, w.endMs - nowMs);
}

/** How early a player can join a group or own session from the bottom nav. */
export const SESSION_JOIN_LEAD_MS = 15 * 60 * 1000;

/** After confirming presence, open Live Training if start is this close (or already on). */
export const PRESENCE_LIVE_REDIRECT_LEAD_MS = 30 * 60 * 1000;

/** From Today's Sessions cards: open Live Training if start is within this window. */
export const TODAY_CARD_LIVE_REDIRECT_LEAD_MS = 60 * 60 * 1000;

/**
 * True when the player should go straight to Live Training after saying yes:
 * session already in its window, starting within 30 minutes, or coach already started.
 */
export function shouldEnterLiveTrainingAfterPresence(
  session,
  nowMs = Date.now(),
  leadMs = PRESENCE_LIVE_REDIRECT_LEAD_MS
) {
  if (!session || isSessionFinalized(session)) return false;
  const w = parseSessionWindow(session, nowMs);
  if (w && nowMs > w.endMs) return false;
  if (isSessionLive(session)) return true;
  if (!w) return false;
  return nowMs >= w.startMs - leadMs && nowMs <= w.endMs;
}

/** Coach-planned group session (not a player-owned personal table booking). */
export function isGroupCoachedSession(session) {
  if (!session?.ts_id) return false;
  return !session.ts_owner_player_id;
}

/**
 * True from 15 minutes before start until the scheduled window ends.
 * Group-coached nights and player-owned bookings (alone or with a partner).
 */
export function isSessionJoinableSoon(
  session,
  nowMs = Date.now(),
  leadMs = SESSION_JOIN_LEAD_MS
) {
  if (!session || isSessionFinalized(session)) return false;
  if (!session.ts_id) return false;
  const w = parseSessionWindow(session, nowMs);
  if (!w) return false;
  return nowMs >= w.startMs - leadMs && nowMs <= w.endMs;
}

/**
 * Pick best eligible session: prefer live/paused, then earliest window start.
 */
export function pickBestEligibleSession(candidates) {
  if (!Array.isArray(candidates) || !candidates.length) return null;
  const live = candidates.filter((c) => isSessionLive(c.session));
  const pool = live.length ? live : candidates;
  pool.sort((a, b) => {
    const wa = parseSessionWindow(a.session);
    const wb = parseSessionWindow(b.session);
    return (wa?.startMs || 0) - (wb?.startMs || 0);
  });
  return pool[0] || null;
}
